import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

export function getAdminToken() {
  return localStorage.getItem("li_admin_token");
}

export function setAdminToken(token) {
  localStorage.setItem("li_admin_token", token);
}

export function clearAdminToken() {
  localStorage.removeItem("li_admin_token");
}

export const adminApi = axios.create({ baseURL: API });
adminApi.interceptors.request.use((config) => {
  const t = getAdminToken();
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

export const PROFILE_LABELS = {
  porteuse: "La Porteuse",
  suradaptee: "La Suradaptée",
  eteinte: "L'Éteinte",
  deconnectee: "La Déconnectée",
};

export const PROFILE_BADGE = {
  porteuse: "bg-[#F0E6D2] text-[#756240]",
  suradaptee: "bg-[#E6E2EE] text-[#5B5370]",
  eteinte: "bg-[#E0E5E5] text-[#4A5D5D]",
  deconnectee: "bg-[#F5E0D8] text-[#7A4D3E]",
};

export const PROFILE_DESCRIPTIONS = {
  porteuse:
    "Vous portez beaucoup pour les autres. Vous anticipez, prenez en charge, et vous oubliez progressivement. Votre force est immense, mais elle se nourrit aussi de votre propre énergie.",
  suradaptee:
    "Vous vous adaptez en permanence à votre environnement. Vous évitez les tensions, cherchez à préserver l'harmonie, et avez du mal à dire non. Votre sensibilité aux autres est un don qui peut aussi devenir un poids.",
  eteinte:
    "Vous continuez d'avancer malgré une fatigue émotionnelle profonde. Vous tenez, vous faites, mais quelque chose en vous s'est mis en veille. Une lumière qui demande à être ranimée doucement.",
  deconnectee:
    "Vous avez perdu le fil de ce que vous voulez vraiment pour vous-même. Vos besoins, vos désirs, vos envies sont devenus flous. Une reconnexion à soi est possible, en douceur.",
};

export const PROFILE_DETAILS = {
  porteuse: {
    behaviors: [
      "Anticiper les besoins de tout le monde avant les siens",
      "Avoir du mal à déléguer ou à demander de l'aide",
      "Se sentir responsable de l'équilibre familial ou professionnel",
      "Culpabiliser dès qu'elle ralentit",
    ],
    drains: [
      "Une charge mentale invisible et permanente",
      "L'impression d'être indispensable, qui empêche le repos",
      "Le manque de reconnaissance pour tout ce qu'elle porte",
    ],
    needs: [
      "Apprendre à déposer une partie du poids sans culpabilité",
      "Reconnaître ses propres limites comme un cadeau et non une faiblesse",
      "Se réautoriser à recevoir, à demander, à être portée à son tour",
    ],
    visio_focus:
      "Travailler la dynamique de sur-responsabilité, identifier d'où vient ce besoin de tout porter, et ouvrir un espace pour qu'elle puisse simplement exister sans devoir.",
    opening_script:
      "« Avant tout, je voudrais vous remercier pour la confiance que vous avez déposée dans ce diagnostic. Ce qui ressort de vos réponses, c'est une dynamique très précieuse mais aussi très exigeante : celle d'une femme qui porte beaucoup, pour beaucoup. Je vous propose qu'on prenne le temps ensemble de regarder ce que cela vous coûte, et surtout ce qu'on peut commencer à alléger. Avant de commencer, dites-moi : qu'est-ce qui vous a amenée à faire ce diagnostic aujourd'hui ? »",
  },
  suradaptee: {
    behaviors: [
      "S'ajuster en permanence à l'humeur des autres",
      "Éviter les conflits, parfois au détriment de ses propres besoins",
      "Dire oui par réflexe, puis le regretter",
      "Trouver difficile de prendre une place sans se justifier",
    ],
    drains: [
      "Une vigilance émotionnelle constante envers l'entourage",
      "La peur diffuse de décevoir ou de déranger",
      "Le sentiment d'être plus loyale aux autres qu'à elle-même",
    ],
    needs: [
      "Réapprendre à habiter sa propre voix",
      "Distinguer ce qu'elle veut de ce qu'elle pense devoir vouloir",
      "Poser des limites bienveillantes mais fermes, sans excuse",
    ],
    visio_focus:
      "Explorer le coût de la suradaptation, retrouver le 'non' juste, et ancrer une posture qui respecte sa propre singularité.",
    opening_script:
      "« Merci infiniment d'avoir pris ce moment pour vous, c'est déjà un beau pas. Ce qui ressort dans vos réponses, c'est une grande capacité d'adaptation aux autres — un don véritable, qui peut aussi vous éloigner doucement de vous-même. Je vous propose qu'on explore ensemble, sans aucun jugement, ce que cela vous coûte au quotidien. Pour commencer, racontez-moi : qu'est-ce qui vous a donné envie de prendre ce rendez-vous ? »",
  },
  eteinte: {
    behaviors: [
      "Continuer à faire ce qu'il faut malgré une fatigue profonde",
      "Sourire à l'extérieur tout en étant vide à l'intérieur",
      "Perdre progressivement le goût pour ce qui l'animait avant",
      "Avoir du mal à ressentir des émotions de joie ou d'enthousiasme",
    ],
    drains: [
      "Une fatigue émotionnelle qui ne se résout pas avec le sommeil",
      "L'habitude de tenir, qui éloigne du ressenti réel",
      "Le sentiment d'avoir mis sa propre vie sur pause",
    ],
    needs: [
      "Reconnaître l'épuisement comme un signal, pas une faiblesse",
      "Ralentir vraiment, en douceur, sans tout réorganiser d'un coup",
      "Réveiller progressivement la sensation, le plaisir, l'élan",
    ],
    visio_focus:
      "Évaluer la profondeur de l'épuisement, créer un premier espace de relâchement, et tracer les contours d'un chemin de remise en lumière progressive.",
    opening_script:
      "« Je vous remercie de m'accorder ce temps. Ce que je perçois à travers vos réponses, c'est une femme qui tient depuis longtemps, peut-être trop longtemps. Ici, vous n'avez rien à prouver, rien à tenir. On va simplement prendre le temps d'écouter ce qui est là, en douceur. Avant qu'on entre dans le détail, est-ce que vous pouvez me dire, avec vos mots, comment vous vous sentez ces derniers temps ? »",
  },
  deconnectee: {
    behaviors: [
      "Avoir du mal à répondre quand on lui demande ce qu'elle veut",
      "Avancer en pilote automatique, sans véritable direction",
      "Se sentir étrangère à ses propres choix passés",
      "Ressentir un flou intérieur diffus, difficile à nommer",
    ],
    drains: [
      "L'absence de repères intérieurs clairs",
      "La sensation d'être à côté de sa vie",
      "Le doute permanent sur ce qui lui ferait du bien",
    ],
    needs: [
      "Réapprendre à s'écouter, même quand la réponse est floue",
      "Reconnecter avec le corps, les sensations, les goûts simples",
      "Reconstruire une boussole intérieure, pas à pas",
    ],
    visio_focus:
      "Aider à ressentir avant de penser, ouvrir un espace pour explorer ce qui résonne vraiment, et amorcer une reconnexion concrète à soi.",
    opening_script:
      "« Merci d'être là aujourd'hui. Ce qui ressort de vos réponses, c'est un sentiment de flou intérieur, comme si la boussole s'était brouillée. C'est une étape importante, et elle n'a rien d'anormal — elle ouvre simplement la porte d'un nouveau chapitre. On va y aller doucement, sans chercher à tout clarifier d'un coup. Pour commencer : si vous deviez mettre un mot sur ce que vous ressentez en ce moment, lequel choisiriez-vous ? »",
  },
};
